import { useCallback, useEffect, useMemo, useState } from 'react';
import { bangkokDate, emptySale, type Branch, type DailySale, type Region } from './domain';
import { clearStore, loadLocalState, putValue } from './db';
import { flushOutbox, recordEvent } from './sync';

export function useAppStore() {
  const [ready, setReady] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [sales, setSales] = useState<DailySale[]>([]);
  const today = useMemo(() => bangkokDate(), []);

  useEffect(() => {
    void loadLocalState().then(async (state) => {
      let nextBranches = state.branches;
      let nextSales = state.sales;
      if (navigator.onLine) {
        try {
          const response = await fetch('/api/bootstrap', { headers: { Accept: 'application/json' } });
          if (response.ok) {
            const remote = await response.json() as { branches?: Array<Record<string, unknown>>; sales?: Array<Record<string, unknown>> };
            const remoteBranches: Branch[] = (remote.branches ?? []).map((row) => ({
              id: String(row.id), name: String(row.name), region: String(row.region) as Region,
              sortOrder: Number(row.sort_order), isActive: Boolean(row.is_active),
              createdAt: String(row.created_at), updatedAt: String(row.updated_at)
            }));
            const remoteSales: DailySale[] = (remote.sales ?? []).map((row) => ({
              id: String(row.id), salesDate: String(row.sales_date), branchId: String(row.branch_id),
              branchNameSnapshot: String(row.branch_name_snapshot), regionSnapshot: String(row.region_snapshot) as Region,
              salesAmount: row.sales_amount === null ? null : Number(row.sales_amount), paidComplete: Boolean(row.paid_complete),
              posComplete: Boolean(row.pos_complete), billComplete: Boolean(row.bill_complete), stockAttached: Boolean(row.stock_attached),
              salesLinkUpdated: Boolean(row.sales_link_updated), dailyInspectionAttached: Boolean(row.daily_inspection_attached),
              unpluggedPhotoConfirmed: Boolean(row.unplugged_photo_confirmed), revision: Number(row.revision), updatedAt: String(row.client_updated_at)
            }));
            nextBranches = [...nextBranches];
            for (const branch of remoteBranches) {
              const local = nextBranches.find((item) => item.id === branch.id);
              if (!local || branch.updatedAt > local.updatedAt) nextBranches = [...nextBranches.filter((item) => item.id !== branch.id), branch];
            }
            nextSales = [...nextSales];
            for (const sale of remoteSales) {
              const local = nextSales.find((item) => item.salesDate === sale.salesDate && item.branchId === sale.branchId);
              if (!local || sale.revision > local.revision) nextSales = [...nextSales.filter((item) => !(item.salesDate === sale.salesDate && item.branchId === sale.branchId)), sale];
            }
            await Promise.all([...nextBranches.map((branch) => putValue('branches', branch)), ...nextSales.map((sale) => putValue('sales', sale))]);
          }
        } catch { /* local data remains authoritative while offline/unreachable */ }
      }
      setBranches(nextBranches.sort((a, b) => a.sortOrder - b.sortOrder));
      setSales(nextSales);
      setReady(true);
      void flushOutbox();
    });
    const online = () => void flushOutbox();
    window.addEventListener('online', online);
    const visible = () => { if (!document.hidden) void flushOutbox(); };
    document.addEventListener('visibilitychange', visible);
    return () => { window.removeEventListener('online', online); document.removeEventListener('visibilitychange', visible); };
  }, []);

  const activeBranches = useMemo(() => branches.filter((branch) => branch.isActive).sort((a, b) => a.sortOrder - b.sortOrder), [branches]);

  const addBranch = useCallback(async (name: string, region: Region) => {
    const now = new Date().toISOString();
    const branch: Branch = { id: crypto.randomUUID(), name: name.trim(), region, sortOrder: activeBranches.length + 1, isActive: true, createdAt: now, updatedAt: now };
    await putValue('branches', branch); setBranches((list) => [...list, branch]);
    await recordEvent('branch_created', { name: branch.name, region }, branch.id);
  }, [activeBranches.length]);

  const updateBranch = useCallback(async (branch: Branch) => {
    const next = { ...branch, name: branch.name.trim(), updatedAt: new Date().toISOString() };
    await putValue('branches', next); setBranches((list) => list.map((item) => item.id === next.id ? next : item));
    await recordEvent('branch_updated', { name: next.name, region: next.region, sortOrder: next.sortOrder }, next.id);
  }, []);

  const archiveBranch = useCallback(async (id: string) => {
    const branch = branches.find((item) => item.id === id); if (!branch) return;
    const next = { ...branch, isActive: false, updatedAt: new Date().toISOString() };
    await putValue('branches', next); setBranches((list) => list.map((item) => item.id === id ? next : item));
    await recordEvent('branch_deactivated', { name: branch.name }, id);
  }, [branches]);

  const moveBranch = useCallback(async (id: string, delta: number) => {
    const list = activeBranches.slice(); const from = list.findIndex((item) => item.id === id); const to = from + delta;
    if (from < 0 || to < 0 || to >= list.length) return;
    [list[from], list[to]] = [list[to], list[from]];
    const updates = list.map((branch, index) => ({ ...branch, sortOrder: index + 1, updatedAt: new Date().toISOString() }));
    await Promise.all(updates.map((branch) => putValue('branches', branch)));
    setBranches((all) => all.map((branch) => updates.find((item) => item.id === branch.id) ?? branch));
  }, [activeBranches]);

  const saleFor = useCallback((branch: Branch, date = today) => sales.find((sale) => sale.branchId === branch.id && sale.salesDate === date) ?? emptySale(branch, date), [sales, today]);

  const saveSale = useCallback(async (branch: Branch, patch: Partial<DailySale>, kind: 'sale_changed' | 'checklist_changed') => {
    const current = sales.find((sale) => sale.branchId === branch.id && sale.salesDate === today) ?? emptySale(branch, today);
    const next: DailySale = { ...current, ...patch, branchNameSnapshot: branch.name, regionSnapshot: branch.region, revision: current.revision + 1, updatedAt: new Date().toISOString() };
    await putValue('sales', next);
    setSales((list) => [...list.filter((sale) => !(sale.branchId === branch.id && sale.salesDate === today)), next]);
    await recordEvent(kind, { fields: Object.keys(patch), revision: next.revision }, branch.id);
  }, [sales, today]);

  const resetToday = useCallback(async () => {
    const resets = activeBranches.map((branch) => ({ ...emptySale(branch, today), revision: (sales.find((sale) => sale.branchId === branch.id && sale.salesDate === today)?.revision ?? 0) + 1 }));
    await Promise.all(resets.map((sale) => putValue('sales', sale)));
    setSales((list) => [...list.filter((sale) => sale.salesDate !== today), ...resets]);
    await recordEvent('daily_sales_reset', { date: today });
  }, [activeBranches, sales, today]);

  const clearEverything = useCallback(async () => {
    await Promise.all(['branches', 'sales', 'outbox'].map(clearStore)); setBranches([]); setSales([]);
  }, []);

  return { ready, today, branches, activeBranches, sales, addBranch, updateBranch, archiveBranch, moveBranch, saleFor, saveSale, resetToday, clearEverything };
}
